import React, {useEffect, useMemo, useState} from "react";
import {Button, Form, Modal, Table} from "react-bootstrap";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import {toastError, toastSuccess} from "../../../../helpers/Toast";

const emptyTrip = employeeId => ({
  id: null,
  employeeId,
  businessTripTypeDTO: null,
  transportationDTO: null,
  dateFrom: "",
  dateTo: "",
  destination: "",
  registrationNumber: "",
  distanceKm: 0,
  description: "",
  cleared: false
});

const EmployeeBusinessTripsCard = ({employeeId}) => {
  const axiosPrivate = useAxiosPrivate();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [transportations, setTransportations] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const [tripsResponse, typesResponse, transportationResponse] = await Promise.all([
        axiosPrivate.get(`/business-trip/employee/${employeeId}`),
        axiosPrivate.get("/business-trip-types"),
        axiosPrivate.get("/transportations")
      ]);
      setItems(tripsResponse.data || []);
      setTypes(typesResponse.data || []);
      setTransportations(transportationResponse.data || []);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać wyjazdów służbowych.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [employeeId]);

  const sortedItems = useMemo(() => [...items].sort((a, b) => String(b.dateFrom || "").localeCompare(String(a.dateFrom || ""))), [items]);

  const openNew = () => setEditing(emptyTrip(employeeId));
  const openEdit = item => setEditing({...item});

  const update = (field, value) => setEditing(prev => ({...prev, [field]: value}));

  const save = async () => {
    if (!editing.businessTripTypeDTO?.id || !editing.dateFrom || !editing.dateTo || !editing.destination?.trim()) {
      toastError("Uzupełnij typ, daty i miejsce docelowe wyjazdu.");
      return;
    }
    try {
      const payload = {
        ...editing,
        employeeId,
        businessTripTypeDTO: {id: editing.businessTripTypeDTO.id},
        transportationDTO: editing.transportationDTO?.id ? {id: editing.transportationDTO.id} : null,
        distanceKm: Number(editing.distanceKm || 0)
      };
      if (editing.id) await axiosPrivate.put(`/business-trip/${editing.id}`, payload);
      else await axiosPrivate.post("/business-trip", payload);
      setEditing(null);
      await load();
      toastSuccess("Wyjazd służbowy został zapisany.");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się zapisać wyjazdu służbowego.");
    }
  };

  const remove = async id => {
    if (!window.confirm("Usunąć ten wyjazd służbowy?")) return;
    try {
      await axiosPrivate.delete(`/business-trip/${id}`);
      await load();
      toastSuccess("Wyjazd służbowy został usunięty.");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się usunąć wyjazdu służbowego.");
    }
  };

  const dateTimeLabel = value => value ? String(value).replace("T", " ").slice(0, 16) : "—";

  return (
    <div className="card" id="business-trips">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <strong>Wyjazdy służbowe</strong>
          <div className="small text-muted">Historia delegacji, transportu i rozliczenia.</div>
        </div>
        <Button size="sm" onClick={openNew} disabled={!employeeId}>Dodaj wyjazd</Button>
      </div>
      <div className="card-body p-0">
        {loading ? <div className="p-3 text-muted">Pobieranie danych…</div> : sortedItems.length === 0 ? (
          <div className="p-3 text-muted">Brak zapisanych wyjazdów służbowych.</div>
        ) : (
          <div className="table-responsive">
            <Table size="sm" hover className="mb-0">
              <thead><tr><th>Od</th><th>Do</th><th>Typ</th><th>Cel</th><th>Transport</th><th>km</th><th>Rozliczony</th><th></th></tr></thead>
              <tbody>{sortedItems.map(item => (
                <tr key={item.id}>
                  <td>{dateTimeLabel(item.dateFrom)}</td>
                  <td>{dateTimeLabel(item.dateTo)}</td>
                  <td>{item.businessTripTypeDTO?.name || item.businessTripTypeDTO?.shortName || "—"}</td>
                  <td>{item.destination}</td>
                  <td>{item.transportationDTO?.name || "—"}</td>
                  <td>{item.distanceKm || 0}</td>
                  <td>{item.cleared ? "TAK" : "NIE"}</td>
                  <td className="text-nowrap">
                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(item)}>Edytuj</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(item.id)}>Usuń</Button>
                  </td>
                </tr>
              ))}</tbody>
            </Table>
          </div>
        )}
      </div>

      <Modal show={!!editing} onHide={() => setEditing(null)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>{editing?.id ? "Edycja wyjazdu służbowego" : "Nowy wyjazd służbowy"}</Modal.Title></Modal.Header>
        <Modal.Body>
          {editing && <div className="row g-3">
            <div className="col-md-6">
              <Form.Label>Typ wyjazdu</Form.Label>
              <Form.Select value={editing.businessTripTypeDTO?.id || ""} onChange={e => update("businessTripTypeDTO", types.find(x => String(x.id) === e.target.value) || null)}>
                <option value="">-- wybierz --</option>
                {types.map(item => <option key={item.id} value={item.id}>{item.name || item.shortName}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <Form.Label>Środek transportu</Form.Label>
              <Form.Select value={editing.transportationDTO?.id || ""} onChange={e => update("transportationDTO", transportations.find(x => String(x.id) === e.target.value) || null)}>
                <option value="">-- brak / nie dotyczy --</option>
                {transportations.map(item => <option key={item.id} value={item.id}>{item.name || item.shortName}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-6"><Form.Label>Od</Form.Label><Form.Control type="datetime-local" value={String(editing.dateFrom || "").slice(0,16)} onChange={e => update("dateFrom", e.target.value)}/></div>
            <div className="col-md-6"><Form.Label>Do</Form.Label><Form.Control type="datetime-local" value={String(editing.dateTo || "").slice(0,16)} onChange={e => update("dateTo", e.target.value)}/></div>
            <div className="col-md-8"><Form.Label>Miejsce docelowe</Form.Label><Form.Control value={editing.destination || ""} onChange={e => update("destination", e.target.value)}/></div>
            <div className="col-md-4"><Form.Label>Dystans [km]</Form.Label><Form.Control type="number" min="0" step="0.1" value={editing.distanceKm ?? 0} onChange={e => update("distanceKm", e.target.value)}/></div>
            <div className="col-md-6"><Form.Label>Nr rejestracyjny</Form.Label><Form.Control value={editing.registrationNumber || ""} onChange={e => update("registrationNumber", e.target.value)}/></div>
            <div className="col-md-6 d-flex align-items-end"><Form.Check type="switch" label="Wyjazd rozliczony" checked={!!editing.cleared} onChange={e => update("cleared", e.target.checked)}/></div>
            <div className="col-12"><Form.Label>Opis / uwagi</Form.Label><Form.Control as="textarea" rows={3} value={editing.description || ""} onChange={e => update("description", e.target.value)}/></div>
          </div>}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setEditing(null)}>Anuluj</Button><Button onClick={save}>Zapisz</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeeBusinessTripsCard;
